import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { CustomerAvailabilityLink, PublicCustomerInfo } from '@/types/availability';
import { addDays } from 'date-fns';
import { sendVideoCallNotification } from '@/lib/services/whatsapp';

export class CustomerAvailabilityService {
  
  /**
   * Generate a customer-specific availability link
   */
  static async generateCustomerLink(
    coachId: string,
    customerId: string,
    customerName: string,
    customerPhone?: string,
    expiryDays: number = 1
  ): Promise<CustomerAvailabilityLink> {
    try {
      // Generate unique link ID
      const linkId = `${coachId}-${customerId}-${Date.now()}`;
      
      const customerLink: CustomerAvailabilityLink = {
        id: linkId,
        coachId,
        customerId,
        customerName,
        customerPhone,
        createdAt: serverTimestamp() as Timestamp,
        expiresAt: Timestamp.fromDate(addDays(new Date(), expiryDays)),
        isActive: true
      };
      
      // Save to Firestore
      const linkRef = doc(db, 'customerAvailabilityLinks', linkId);
      await setDoc(linkRef, customerLink);
      
      return customerLink;
    } catch (error) {
      console.error('Error generating customer link:', error);
      throw error;
    }
  }
  
  /**
   * Get customer link by ID and validate it
   */
  static async getCustomerLink(linkId: string): Promise<CustomerAvailabilityLink | null> {
    try {
      const linkDoc = await getDoc(doc(db, 'customerAvailabilityLinks', linkId));
      
      if (!linkDoc.exists()) {
        return null;
      }
      
      const linkData = linkDoc.data() as CustomerAvailabilityLink;
      
      // Check if link is expired
      const now = new Date();
      const expiryDate = linkData.expiresAt.toDate();
      
      if (now > expiryDate || !linkData.isActive) {
        return null;
      }
      
      return linkData;
    } catch (error) {
      console.error('Error getting customer link:', error);
      return null;
    }
  }
  
  
  /**
   * Get public customer info from link (no authentication required)
   */
  static async getPublicCustomerInfo(linkId: string): Promise<PublicCustomerInfo | null> {
    try {
      const customerLink = await this.getCustomerLink(linkId);
      
      if (!customerLink) {
        return null;
      }
      
      return {
        customerId: customerLink.customerId,
        customerName: customerLink.customerName,
        customerPhone: customerLink.customerPhone,
        linkId: customerLink.id
      };
    } catch (error) {
      console.error('Error getting public customer info:', error);
      return null;
    }
  }
  
  /**
   * Send availability link to customer via WhatsApp
   * Uses a customer-specific link so they don't need to login
   */
  static async sendAvailabilityLinkToCustomer(
    coachId: string,
    coachName: string,
    customerId: string,
    customerName: string,
    customerPhone: string,
    language: 'en' | 'ar' = 'en',
    appointmentInfo?: {
      date: Date;
      status: string;
    }
  ): Promise<{ success: boolean; linkId?: string; error?: string }> {
    try {
      // Generate customer-specific link (expires in 1 day)
      const customerLink = await this.generateCustomerLink(
        coachId,
        customerId,
        customerName,
        customerPhone,
        1 // 1 day expiry
      );
      
      // Build customer-specific URL (no login required)
      const baseUrl = window.location.origin;
      const availabilityLink = `${baseUrl}/clinic/${coachId}/${customerLink.id}`;
      
      // Create WhatsApp message with appointment info
      let message: string;
      
      if (appointmentInfo) {
        const appointmentDate = appointmentInfo.date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        message = language === 'ar' 
          ? `📅 تذكير بالموعد\n\nمرحباً ${customerName}! 👋\n\nموعدك مع المدرب ${coachName}:\n📅 ${appointmentDate}\n📋 الحالة: ${appointmentInfo.status}\n\n🎥 المدرب متاح الآن للمكالمات المرئية.\n\nانقر على هذا الرابط للانضمام:\n${availabilityLink}`
          : `📅 Appointment Reminder\n\nHi ${customerName}! 👋\n\nYour appointment with Coach ${coachName}:\n📅 ${appointmentDate}\n📋 Status: ${appointmentInfo.status}\n\n🎥 Coach is available for video calls.\n\nClick this link to join:\n${availabilityLink}`;
      } else {
        message = language === 'ar' 
          ? `🎥 مرحباً ${customerName}! 👋\n\nالمدرب ${coachName} متاح الآن للمكالمات المرئية.\n\nانقر على هذا الرابط للانضمام:\n${availabilityLink}`
          : `🎥 Hi ${customerName}! 👋\n\nCoach ${coachName} is available for video calls.\n\nClick this link to join:\n${availabilityLink}`;
      }
      
      // Send WhatsApp message
      const result = await sendVideoCallNotification(
        customerPhone,
        customerName,
        availabilityLink,
        language
      );
      
      if (result.success) {
        return { 
          success: true, 
          linkId: customerLink.id
        };
      } else {
        return { 
          success: false, 
          error: 'Failed to send WhatsApp message' 
        };
      }
    } catch (error) {
      console.error('Error sending availability link:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }
  
  /**
   * Get all active links for a coach
   */
  static async getCoachActiveLinks(coachId: string): Promise<CustomerAvailabilityLink[]> {
    try {
      const linksQuery = query(
        collection(db, 'customerAvailabilityLinks'),
        where('coachId', '==', coachId),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(linksQuery);
      const links: CustomerAvailabilityLink[] = [];
      
      querySnapshot.forEach((doc) => {
        const linkData = doc.data() as CustomerAvailabilityLink;
        
        // Check if not expired
        const now = new Date();
        const expiryDate = linkData.expiresAt.toDate();
        
        if (now <= expiryDate) {
          links.push(linkData);
        }
      });
      
      return links;
    } catch (error) {
      console.error('Error getting coach active links:', error);
      return [];
    }
  }
  
  /**
   * Deactivate a customer link
   */
  static async deactivateLink(linkId: string): Promise<void> {
    try {
      const linkRef = doc(db, 'customerAvailabilityLinks', linkId);
      await updateDoc(linkRef, {
        isActive: false
      });
    } catch (error) {
      console.error('Error deactivating link:', error);
      throw error;
    }
  }
}
